/**
 * Shared donation validators and constants.
 *
 * Lives in api-contracts so client UI, RPC handlers, and DB-layer code all enforce
 * identical bounds. Cents are integers (Stripe-style) — the bounds chosen here
 * mirror the constraints set on the DB-side `live_event_donation_settings` table
 * (migration 069). Keep them in sync if either side changes.
 */

export const DONATION_MIN_CENTS = 50;
export const DONATION_MAX_CENTS = 9999900;
export const DONATION_PRESET_COUNT = 5;
export const DONATION_DEFAULT_PRESETS_CENTS = [10000, 7500, 5000, 2500, 1000];

export function validateDonationAmount(amountCents: number): string | null {
  if (!Number.isFinite(amountCents)) return "Amount must be a number";
  if (!Number.isInteger(amountCents)) return "Amount must be an integer (cents)";
  if (amountCents < DONATION_MIN_CENTS) return `Amount must be at least ${DONATION_MIN_CENTS} cents`;
  if (amountCents > DONATION_MAX_CENTS) return `Amount must be at most ${DONATION_MAX_CENTS} cents`;
  return null;
}

export function validateDonationPresets(presets: number[]): string | null {
  if (!Array.isArray(presets)) return "Presets must be an array";
  if (presets.length !== DONATION_PRESET_COUNT) return `Presets must have exactly 5 entries`;
  for (let i = 0; i < presets.length; i++) {
    const p = presets[i];
    if (p === undefined || !Number.isFinite(p)) return `Preset ${i + 1} must be a number`;
    if (!Number.isInteger(p)) return `Preset ${i + 1} must be an integer (cents)`;
    if (p < DONATION_MIN_CENTS) return `Preset ${i + 1} must be at least ${DONATION_MIN_CENTS} cents`;
  }
  return null;
}

export function validateDonationTarget(targetCents: number): string | null {
  if (!Number.isFinite(targetCents)) return "Target must be a number";
  if (!Number.isInteger(targetCents)) return "Target must be an integer (cents)";
  if (targetCents <= 0) return "Target must be greater than 0";
  return null;
}
